import { TabPanel } from '@headlessui/react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import SkeletonLoader from 'tiny-skeleton-loader-react';
import useGetRequest from '../../../api/useGetRequest';

export default function AddressDetails({ isFocused, addressId }) {
  const [shouldFetch, setShouldFetch] = useState(false);

  const { data, isLoading, mutate } = useGetRequest(
    addressId && shouldFetch ? `/addresses/${addressId}` : null,
    0
  );

  useEffect(() => {
    if (isFocused && addressId && !shouldFetch) {
      setShouldFetch(true);
    }
  }, [isFocused, addressId]);

  if (isLoading) {
    return (
      <TabPanel>
        <div className="tw-mt-6">
          <div className="tw-grid tw-grid-cols-2 tw-gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i}>
                <SkeletonLoader
                  width={150}
                  height={15}
                  style={{ borderRadius: 4, marginBottom: 8 }}
                />
                <SkeletonLoader
                  width="100%"
                  height={20}
                  style={{ borderRadius: 4 }}
                />
              </div>
            ))}
          </div>
        </div>
      </TabPanel>
    );
  }

  if (!data) {
    return (
      <TabPanel>
        <div className="tw-mt-8 tw-text-center tw-text-gray-500">
          <p>Aucune donnée disponible</p>
        </div>
      </TabPanel>
    );
  }

  return (
    <TabPanel>
      <div className="tw-mt-6">
        <div className="tw-grid tw-grid-cols-2 tw-gap-6">
          {/* Colonne gauche */}
          <div>
            <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
              Informations générales
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Clé adresse
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.address_key || '-'}
              </div>
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Nom
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.name || '-'}
              </div>
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Email
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.email || '-'}
              </div>
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Date de création
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.creation_date
                  ? moment(data.creation_date).format('DD/MM/YYYY [à] HH:mm')
                  : '-'}
              </div>
            </div>

            {data?.updated_date && (
              <div className="tw-mb-3">
                <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                  Dernière modification
                </div>
                <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                  {moment(data.updated_date).format('DD/MM/YYYY [à] HH:mm')}
                </div>
              </div>
            )}
          </div>

          {/* Colonne droite */}
          <div>
            <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
              Adresse
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Rue
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.address_number && data?.street
                  ? `${data.address_number} ${data.street}`
                  : data?.street || data?.address_number || '-'}
              </div>
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Code postal
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.zipcode || '-'}
              </div>
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Ville
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.city || '-'}
              </div>
            </div>

            {data?.region && (
              <div className="tw-mb-3">
                <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                  Région
                </div>
                <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                  {data.region}
                </div>
              </div>
            )}

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Pays
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.country || '-'}
              </div>
            </div>

            {data?.latitude && data?.longitude && (
              <div className="tw-mb-3">
                <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                  Coordonnées GPS
                </div>
                <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                  {data.latitude}, {data.longitude}
                </div>
              </div>
            )}
          </div>

          {/* Section téléphones */}
          <div>
            <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
              Numéros de téléphone
            </div>

            {data?.phone_numbers?.length > 0 ? (
              data.phone_numbers.map((phone, index) => (
                <div key={phone.id || index} className="tw-mb-3">
                  <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                    {phone.fullname || phone.name || `Contact ${index + 1}`}
                  </div>
                  <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                    {phone.phone_number_formatted || phone.phone_number || '-'}
                  </div>
                </div>
              ))
            ) : (
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)]">
                Aucun numéro enregistré
              </div>
            )}
          </div>

          {/* Section statistiques */}
          <div>
            <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
              Statistiques
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Livraisons effectuées
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.total_deliveries || '0'}
              </div>
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Livraisons annulées
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.total_canceled_deliveries || '0'}
              </div>
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Dernière livraison
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.last_delivery_date
                  ? moment(data.last_delivery_date).format('DD/MM/YYYY')
                  : '-'}
              </div>
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Réserves associées
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.total_reserves || '0'}
              </div>
            </div>

            <div className="tw-mb-3">
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                Photos associées
              </div>
              <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                {data?.photos_count || '0'}
              </div>
            </div>
          </div>
        </div>

        {/* Commentaires - pleine largeur si présents */}
        {data?.comments?.length > 0 && (
          <div className="tw-mt-6">
            <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
              Commentaires
            </div>
            {data.comments.map((comment, index) => (
              <div key={comment.id || index} className="tw-mb-3">
                <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                  Commentaire {comment.comment_index}
                </div>
                <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                  {comment.comment}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </TabPanel>
  );
}
